import React, { useMemo } from 'react';
import { PvrRecording } from '../../../interfaces';
import { Recording, SingleRecording } from '../../../queries/ats/recordings/types/recording';
import { ErrorBoundary } from '../../error-boundary';
import { PlayerErrorFallback } from '../ats-player/player-error-fallback/player-error-fallback';
import { PlayerLoadingSpinner } from '../ats-player/player-loading-spinner';
import { RecordingClientPlayer } from './recording-client-player';
import { useLegacyRecordings } from './use-legacy-recordings';
import { usePlayRecording } from './use-play-recording';

interface RecordingClientPlayerLoaderProps {
  recording: SingleRecording;
  recordings: Recording[];
  position: number | undefined;
}

export default function RecordingClientPlayerLoader({
  recording,
  recordings,
  position,
}: RecordingClientPlayerLoaderProps) {
  const {
    data,
    bookmark,
    isLoading: isPlayRecordingLoading,
    reportConsumption,
    stopConsumption,
  } = usePlayRecording({ recording, startSession: true });

  const { data: legacyRecordings, isLoading: isLegacyRecordingsLoading } = useLegacyRecordings();

  const isLoading = isPlayRecordingLoading || isLegacyRecordingsLoading;

  const legacyRecording = useMemo(() => {
    const all = legacyRecordings?.reduce<PvrRecording[]>((result, next) => {
      return [...result, next, ...(next.subRecordings || [])];
    }, []);
    return all?.find((x) => x.pvrId === recording.id);
  }, [legacyRecordings, recording.id]);

  if (isLoading || legacyRecordings === undefined) {
    return <PlayerLoadingSpinner />;
  }

  if (legacyRecording === undefined) {
    throw new Error(`Recording with id "${recording.id}" not found.`);
  }

  if (data === undefined) {
    throw new Error('Playback data not found.');
  }

  return (
    <ErrorBoundary fallback={(props) => <PlayerErrorFallback {...props} image={recording.image} />}>
      <RecordingClientPlayer
        data={data}
        recording={recording}
        recordings={recordings}
        legacyRecording={legacyRecording}
        position={position ?? bookmark?.position ?? 0}
        reportConsumption={reportConsumption}
        stopConsumption={stopConsumption}
      />
    </ErrorBoundary>
  );
}
